import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Search, ExternalLink, X, Upload, Image } from 'lucide-react';
import { db, auth } from '../../lib/firebase';
import { collection, getDocs, query, updateDoc, doc, deleteDoc, addDoc } from 'firebase/firestore';


export default function AdminPortfolios() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('');
  const [sortOrder, setSortOrder] = useState('created_at_desc');
  const [portfolios, setPortfolios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [refetch, setRefetch] = useState(0);

  // Check authentication on component mount
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/admin/login');
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const fetchAndSortPortfolios = async () => {
      try {
        setIsLoading(true);
        setError(null);
  
        // Fetch all portfolios without a default order from Firestore
        const portfoliosQuery = query(collection(db, 'portfolios'));
        const querySnapshot = await getDocs(portfoliosQuery);
        let portfoliosData = [];
        querySnapshot.forEach((doc) => {
          portfoliosData.push({ id: doc.id, ...doc.data() });
        });
  
        // Client-side filtering
        if (searchTerm) {
          portfoliosData = portfoliosData.filter(portfolio => 
            portfolio.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
            (portfolio.location && portfolio.location.toLowerCase().includes(searchTerm.toLowerCase()))
          );
        }
  
        if (selectedStyle) {
          portfoliosData = portfoliosData.filter(portfolio => portfolio.style === selectedStyle);
        }
  
        // Client-side sorting
        let sortedData = [...portfoliosData];
        switch (sortOrder) {
          case 'created_at_asc':
            sortedData.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            break;
          case 'title_asc':
            sortedData.sort((a, b) => a.title.localeCompare(b.title));
            break;
          case 'title_desc':
            sortedData.sort((a, b) => b.title.localeCompare(a.title));
            break;
          case 'created_at_desc':
          default:
            sortedData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            break;
        }
  
        setPortfolios(sortedData);
      } catch (err) {
        console.warn('Portfolios fetch failed:', err);
        setPortfolios([]);
        setError('포트폴리오를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchAndSortPortfolios();
  }, [searchTerm, selectedStyle, sortOrder, refetch]);

  const deletePortfolio = async (id) => {
    if (!window.confirm('정말로 이 포트폴리오를 삭제하시겠습니까?')) {
      return;
    }
    try {
      const portfolioDoc = doc(db, 'portfolios', id);
      await deleteDoc(portfolioDoc);
      alert('포트폴리오가 성공적으로 삭제되었습니다.');
      setRefetch(c => c + 1); // Trigger a refetch
    } catch (error) {
      console.error('Portfolio delete failed:', error);
      alert('포트폴리오 삭제 중 오류가 발생했습니다.');
    }
  };

  const styles = ['modern', 'classic', 'minimal', 'industrial', 'scandinavian', 'vintage'];
  const stylesKr = { modern: '모던', classic: '클래식', minimal: '미니멀', industrial: '인더스트리얼', scandinavian: '스칸디나비안', vintage: '빈티지' };
  const housingTypes = ['아파트', '오피스텔', '주택', '빌라', '상가'];
  const areaOptions = ['20평 이하', '20-30평', '30-40평', '40평 이상'];
  const [showModal, setShowModal] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [detailFiles, setDetailFiles] = useState([]);
  const [isDragOverThumbnail, setIsDragOverThumbnail] = useState(false);
  const [isDragOverDetail, setIsDragOverDetail] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingDetails, setUploadingDetails] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const [formData, setFormData] = useState({
    title: '',
    location: '',
    style: 'modern',
    area: '',
    description: '',
    housing_type: '아파트'
  });

  const openModal = (portfolio) => {
    if (portfolio) {
      setEditingPortfolio(portfolio);
      setFormData({
        title: portfolio.title,
        location: portfolio.location,
        style: portfolio.style,
        area: portfolio.area,
        description: portfolio.description || '',
        housing_type: '아파트'
      });
    } else {
      setEditingPortfolio(null);
      setFormData({
        title: '',
        location: '',
        style: 'modern',
        area: '',
        description: '',
        housing_type: '아파트'
      });
    }
    setThumbnailFile(null);
    setDetailFiles([]);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPortfolio(null);
    setThumbnailFile(null);
    setDetailFiles([]);
    setIsDragOverThumbnail(false);
    setIsDragOverDetail(false);
    setUploadingThumbnail(false);
    setUploadingDetails(false);
    setUploadProgress({});
  };

  const setDragState = (type, isDragOver) => {
    if (type === 'thumbnail') setIsDragOverThumbnail(isDragOver);
    else setIsDragOverDetail(isDragOver);
  };

  const validateFiles = (files, config) => {
    const validFiles = [];
    const errors = [];
    files.forEach(file => {
      if (file.size > config.maxSize) {
        errors.push(`${file.name}: 파일 크기가 너무 큽니다 (최대 ${config.maxSize / 1024 / 1024}MB)`);
        return;
      }
      if (!config.allowedTypes.some(type => file.type.startsWith(type))) {
        errors.push(`${file.name}: 지원하지 않는 파일 형식입니다 (이미지 파일만 가능)`);
        return;
      }
      validFiles.push(file);
    });
    if (config.maxFiles && validFiles.length > config.maxFiles) {
      errors.push(`최대 ${config.maxFiles}개의 파일만 업로드할 수 있습니다`);
      return { validFiles: validFiles.slice(0, config.maxFiles), errors };
    }
    return { validFiles, errors };
  };

  const handleDragOver = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setDragState(type, true);
  };

  const handleDragLeave = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) setDragState(type, false);
  };

  const handleThumbnailDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverThumbnail(false);
    const files = Array.from(e.dataTransfer.files);
    const { validFiles, errors } = validateFiles(files, { maxSize: 10 * 1024 * 1024, allowedTypes: ['image/'], maxFiles: 1 });
    if (errors.length > 0) alert(errors.join('\n'));
    if (validFiles.length > 0) setThumbnailFile(validFiles[0]);
  };

  const handleDetailDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverDetail(false);
    const files = Array.from(e.dataTransfer.files);
    const { validFiles, errors } = validateFiles(files, { maxSize: 10 * 1024 * 1024, allowedTypes: ['image/'], maxFiles: 20 });
    if (errors.length > 0) alert(errors.join('\n'));
    if (validFiles.length > 0) setDetailFiles(prev => [...prev, ...validFiles]);
  };

  const uploadImage = (file, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/upload?filename=${file.name}`, true);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          onProgress(percentComplete);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response.url);
          } catch (e) {
            reject(new Error('서버 응답 파싱에 실패했습니다.'));
          }
        } else {
          reject(new Error(`파일 업로드 실패: ${xhr.status} ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => {
        reject(new Error('네트워크 오류로 파일 업로드에 실패했습니다.'));
      };

      xhr.send(file);
    });
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setThumbnailFile(file);
  };

  const handleDetailFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setDetailFiles(prev => [...prev, ...files]);
  };

  const removeDetailFile = (index) => {
    setDetailFiles(prev => prev.filter((_, i) => i !== index));
  };

  const createPortfolio = async (newPortfolio) => {
    try {
      const docRef = await addDoc(collection(db, 'portfolios'), newPortfolio);
      setRefetch(c => c + 1); // Trigger a refetch
      closeModal();
      alert('포트폴리오가 성공적으로 생성되었습니다!');
      return { id: docRef.id, ...newPortfolio };
    } catch (error) {
      console.error('Portfolio create failed:', error);
      alert(`포트폴리오 생성 중 오류가 발생했습니다: ${error.message}`);
      throw error;
    }
  };

  const updatePortfolio = async (updatedPortfolio) => {
    try {
      const portfolioDoc = doc(db, 'portfolios', updatedPortfolio.id);
      await updateDoc(portfolioDoc, updatedPortfolio);
      setRefetch(c => c + 1); // Trigger a refetch
      closeModal();
      return updatedPortfolio;
    } catch (error) {
      console.error('Portfolio update failed:', error);
      alert('포트폴리오 수정 중 오류가 발생했습니다.');
      throw error;
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.location || !formData.style) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }
  
    setIsSaving(true);
    try {
      let thumbnailUrl = editingPortfolio?.thumbnail_url || '';
      let detailImageUrls = editingPortfolio?.images || [];
  
      if (thumbnailFile) {
        setUploadingThumbnail(true);
        thumbnailUrl = await uploadImage(thumbnailFile, (progress) => {
          setUploadProgress(prev => ({ ...prev, thumbnail: progress }));
        });
        setUploadingThumbnail(false);
      }
  
      if (detailFiles.length > 0) {
        setUploadingDetails(true);
        const uploadPromises = detailFiles.map((file, index) =>
          uploadImage(file, (progress) => {
            setUploadProgress(prev => ({ ...prev, [`detail_${index}`]: progress }));
          })
        );
        detailImageUrls = await Promise.all(uploadPromises);
        setUploadingDetails(false);
      }
  
      const portfolioData = {
        ...formData,
        thumbnail_url: thumbnailUrl,
        images: detailImageUrls,
        is_published: true,
        tags: [formData.housing_type],
        created_at: editingPortfolio ? editingPortfolio.created_at : new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
  
      if (editingPortfolio) {
        await updatePortfolio({ id: editingPortfolio.id, ...portfolioData });
      } else {
        await createPortfolio(portfolioData);
      }
    } catch (error) {
      console.error('Portfolio save error:', error);
      alert(`포트폴리오 저장 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setIsSaving(false);
      setUploadingThumbnail(false);
      setUploadingDetails(false);
      setUploadProgress({});
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">포트폴리오 관리</h1>
          <p className="text-sm text-gray-600 mt-1">완성한 작업들을 관리하고 새 포트폴리오를 추가합니다</p>
        </div>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          onClick={() => openModal()}
        >
          새 포트폴리오 추가
        </button>
      </div>

      <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6 flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="제목이나 위치로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={selectedStyle}
          onChange={(e) => setSelectedStyle(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">모든 스타일</option>
          {styles.map(style => (
            <option key={style} value={style}>{stylesKr[style]}</option>
          ))}
        </select>
        <select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="created_at_desc">최신순</option>
          <option value="created_at_asc">오래된순</option>
          <option value="title_asc">제목 (오름차순)</option>
          <option value="title_desc">제목 (내림차순)</option>
        </select>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">포트폴리오를 불러오는 중...</p>
        </div>
      ) : error ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-red-600">데이터를 불러오는 중 오류가 발생했습니다.</p>
        </div>
      ) : portfolios.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-gray-600">등록된 포트폴리오가 없습니다</p>
          <button
            onClick={() => openModal()}
            className="text-blue-600 hover:text-blue-700 text-sm mt-2"
          >
            첫 포트폴리오 등록하기
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolios.map((portfolio) => (
            <div 
              key={portfolio.id} 
              className="bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg hover:border-blue-300"
              onClick={() => window.open(`/project/${portfolio.id}`, '_blank')}
            >
              <div className="aspect-video bg-gray-200">
                {portfolio.thumbnail_url ? (
                  <img
                    src={portfolio.thumbnail_url}
                    alt={portfolio.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop&q=80'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-gray-400 text-4xl mb-2">🏠</div>
                      <p className="text-gray-500 text-sm">이미지 없음</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">{portfolio.style}</span>
                  {portfolio.is_featured && (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">추천</span>
                  )}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{portfolio.title}</h3>
                <p className="text-sm text-gray-600 mb-2">{portfolio.location}</p>
                <p className="text-xs text-gray-500 mb-3">{portfolio.area}</p>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    등록일: {new Date(portfolio.created_at).toLocaleDateString()}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); openModal(portfolio); }}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    ><Edit className="w-4 h-4" /></button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deletePortfolio(portfolio.id); }}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                    ><Trash2 className="w-4 h-4" /></button>
                    <a
                      href={`/project/${portfolio.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    ><ExternalLink className="w-4 h-4" /></a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{editingPortfolio ? '포트폴리오 수정' : '새 포트폴리오 추가'}</h3>
                <button onClick={closeModal} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">제목 *</label>
                    <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">위치 *</label>
                    <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">스타일 *</label>
                    <select value={formData.style} onChange={(e) => setFormData({ ...formData, style: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {Object.entries(stylesKr).map(([value, name]) => ( <option key={value} value={value}>{name}</option> ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">주택타입 *</label>
                    <select value={formData.housing_type} onChange={(e) => setFormData({ ...formData, housing_type: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {housingTypes.map(type => ( <option key={type} value={type}>{type}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">면적</label>
                    <select value={formData.area} onChange={(e) => setFormData({ ...formData, area: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">선택 안함</option>
                      {areaOptions.map(area => (<option key={area} value={area}>{area}</option>))}
                    </select>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">썸네일 이미지</label>
                    <div
                      onDragOver={(e) => handleDragOver(e, 'thumbnail')}
                      onDragLeave={(e) => handleDragLeave(e, 'thumbnail')}
                      onDrop={handleThumbnailDrop}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragOverThumbnail ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
                      role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.querySelector('input[type="file"]')?.click(); }}
                    >
                      <input type="file" accept="image/*" onChange={handleThumbnailChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                      {thumbnailFile ? (
                        <div className="space-y-2">
                          <Image className="w-8 h-8 text-gray-400 mx-auto" />
                          <p className="text-sm text-green-600 font-medium">{thumbnailFile.name}</p>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setThumbnailFile(null);}} className="text-xs text-red-500 hover:text-red-700">제거</button>
                        </div>
                      ) : editingPortfolio?.thumbnail_url ? (
                        <div className="space-y-2">
                          <img src={editingPortfolio.thumbnail_url} alt="현재 썸네일" className="w-20 h-20 object-cover rounded-lg mx-auto border" onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop&q=80'; }} />
                          <p className="text-xs text-gray-500">현재 썸네일 (새 이미지로 교체하려면 드래그하거나 클릭)</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                          <p className="text-sm text-gray-600">썸네일 이미지를 드래그하거나 클릭하여 업로드</p>
                          <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF 파일 지원</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">디테일 이미지 (다중 선택 가능)</label>
                    <div
                      onDragOver={(e) => handleDragOver(e, 'detail')}
                      onDragLeave={(e) => handleDragLeave(e, 'detail')}
                      onDrop={handleDetailDrop}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragOverDetail ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
                      role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.querySelector('input[type="file"]')?.click(); }}
                    >
                      <input type="file" accept="image/*" multiple onChange={handleDetailFilesChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                      <div className="space-y-2">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                        <p className="text-sm text-gray-600">디테일 이미지들을 드래그하거나 클릭하여 업로드</p>
                        <p className="text-xs text-gray-400 mt-1">여러 이미지를 한 번에 선택할 수 있습니다 (PNG, JPG, GIF)</p>
                      </div>
                    </div>
                    {detailFiles.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">선택된 이미지들 ({detailFiles.length}개)</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {detailFiles.map((file, index) => (
                            <div key={index} className="relative group">
                              <Image className="w-8 h-8 text-gray-400 mx-auto" />
                              <p className="text-xs text-gray-600 mt-1 truncate" title={file.name}>{file.name}</p>
                              <button type="button" onClick={() => removeDetailFile(index)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {editingPortfolio?.images && editingPortfolio.images.length > 0 && detailFiles.length === 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">현재 디테일 이미지들 ({editingPortfolio.images.length}개)</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {editingPortfolio.images.slice(0, 9).map((imageUrl, index) => (
                            <div key={index} className="aspect-square">
                              <img src={imageUrl} alt={`디테일 이미지 ${index + 1}`} className="w-full h-full object-cover rounded-lg border" onError={(e) => { e.target.src = `https://images.unsplash.com/photo-${1560472354 + index}-b33ff0c44a43?w=200&h=200&fit=crop&q=80`; }} />
                            </div>
                          ))}
                          {editingPortfolio.images.length > 9 && (
                            <div className="aspect-square bg-gray-100 rounded-lg border flex items-center justify-center">
                              <span className="text-sm text-gray-500 font-medium">+{editingPortfolio.images.length - 9}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">새 이미지를 추가하면 기존 이미지들이 교체됩니다</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
                    <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="포트폴리오에 대한 상세 설명을 입력하세요..." />
                  </div>
                </div>
              </div>

              {(uploadingThumbnail || uploadingDetails) && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-medium text-blue-900 mb-3">이미지 업로드 중...</h4>
                  {uploadingThumbnail && (
                    <div className="mb-3">
                      <div className="flex justify-between text-xs text-blue-700 mb-1">
                        <span>썸네일 이미지</span>
                        <span>{uploadProgress.thumbnail || 0}%</span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2">
                        <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress.thumbnail || 0}%` }}></div>
                      </div>
                    </div>
                  )}
                  {uploadingDetails && (
                    <div>
                      <div className="flex justify-between text-xs text-blue-700 mb-1">
                        <span>디테일 이미지들</span>
                        <span>업로드 중...</span>
                      </div>
                      <div className="space-y-1">
                        {detailFiles.map((_, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <span className="text-xs text-blue-600 w-16">이미지 {index + 1}</span>
                            <div className="flex-1 bg-blue-200 rounded-full h-1.5">
                              <div className="bg-blue-600 h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress[`detail_${index}`] || 0}%` }}></div>
                            </div>
                            <span className="text-xs text-blue-600 w-8">{uploadProgress[`detail_${index}`] || 0}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button onClick={closeModal} disabled={isSaving} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50">취소</button>
                <button onClick={handleSave} disabled={isSaving} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50">
                  {isSaving ? (<div className="flex items-center gap-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>저장 중...</div>) : '저장'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
